import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { openExternalUrl } from "./browser";
import { copyToClipboard } from "./clipboard";
import { isNativePlatform } from "./platform";

export interface SaveFileOptions {
  filename: string;
  data: string;
  mimeType?: string;
}

export interface ShareImageWithTextOptions {
  filename: string;
  data: string;
  text: string;
  mimeType?: string;
  title?: string;
  dialogTitle?: string;
}

function isShareCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    const message = String((error as { message: unknown }).message).toLowerCase();
    return message.includes("cancel") || message.includes("abort");
  }

  return false;
}

function base64ToFile(data: string, filename: string, mimeType: string) {
  const bytes = Uint8Array.from(atob(data), (character) => character.charCodeAt(0));
  const blob = new Blob([bytes], { type: mimeType });
  return new File([blob], filename, { type: mimeType });
}

function sanitizeFilename(filename: string): string {
  return filename.replace(/[^\w.-]+/g, "_");
}

export async function saveAndShareFile({
  filename,
  data,
  mimeType = "application/octet-stream",
}: SaveFileOptions): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  if (isNativePlatform()) {
    const path = sanitizeFilename(filename);

    await Filesystem.writeFile({
      path,
      data,
      directory: Directory.Cache,
    });

    const { uri } = await Filesystem.getUri({
      path,
      directory: Directory.Cache,
    });

    await Share.share({
      title: filename,
      url: uri,
      dialogTitle: "Save or share file",
    });

    return true;
  }

  const bytes = Uint8Array.from(atob(data), (character) => character.charCodeAt(0));
  const blob = new Blob([bytes], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);

  return true;
}

export async function shareImageWithText({
  filename,
  data,
  text,
  mimeType = "image/png",
  title,
  dialogTitle = "Share",
}: ShareImageWithTextOptions): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  if (isNativePlatform()) {
    const path = sanitizeFilename(filename);

    await Filesystem.writeFile({
      path,
      data,
      directory: Directory.Cache,
    });

    const { uri } = await Filesystem.getUri({
      path,
      directory: Directory.Cache,
    });

    const { value: canShare } = await Share.canShare();

    if (!canShare) {
      return false;
    }

    try {
      await copyToClipboard(text);
    } catch {
      // Users can still manually add the caption after sharing the image.
    }

    try {
      await Share.share({
        title,
        text,
        files: [uri],
        dialogTitle,
      });
      return true;
    } catch (error) {
      if (isShareCancelled(error)) {
        return false;
      }
      throw error;
    }
  }

  const file = base64ToFile(data, sanitizeFilename(filename), mimeType);

  if (
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function"
  ) {
    try {
      const payload: ShareData = { text, title };

      if (navigator.canShare({ files: [file] })) {
        payload.files = [file];
      }

      await navigator.share(payload);
      return true;
    } catch (error) {
      if (isShareCancelled(error)) {
        return false;
      }
      throw error;
    }
  }

  const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
  await openExternalUrl(waUrl);
  return true;
}
